import React, { useState, useEffect } from "react";
import { X, Bug, Upload, Paperclip, Check } from "lucide-react";
import { db } from "../../firebase/config";
import { collection, addDoc } from "firebase/firestore";

export default function SendToDeveloperModal({ isOpen, onClose, userName }) {
  const [ticketType, setTicketType] = useState("Bug");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTicketType("Bug");
      setDescription("");
      setAttachments([]);
      setError("");
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setError("");
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    if (attachments.length + selectedFiles.length > 5) {
      setError(`You can attach up to 5 files maximum. You currently have ${attachments.length} attached.`);
    }

    const availableSlots = 5 - attachments.length;
    const filesToProcess = selectedFiles.slice(0, availableSlots);
    const allowedExts = ["jpg", "jpeg", "png", "docx", "xlsx", "pdf"];

    filesToProcess.forEach(file => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!allowedExts.includes(ext)) {
        setError(`Unsupported format in "${file.name}". Allowed: jpg, png, docx, xlsx, pdf.`);
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError(`"${file.name}" exceeds the 5MB file size limit.`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments(prev => {
          if (prev.length >= 5) return prev;
          return [
            ...prev,
            {
              name: file.name,
              type: file.type,
              size: file.size,
              data: reader.result // Base64 data URL
            }
          ];
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be re-selected if removed
    e.target.value = "";
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAttachments(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError("Please describe the bug or feature request.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      // Generate a unique 6-digit ticket number
      const randomId = Math.floor(100000 + Math.random() * 900000);
      const ticketNumber = `TKT-${randomId}`;

      const ticketData = {
        ticketNumber,
        type: ticketType,
        description: description.trim(),
        status: "Open",
        createdBy: userName || "System User",
        createdAt: new Date().toISOString(),
        attachments: attachments,
        attachment: attachments.length > 0 ? attachments[0] : null
      };

      const ticketDocRef = await addDoc(collection(db, "tickets"), ticketData);

      // Send alert notification to all Developer role members
      await addDoc(collection(db, "notifications"), {
        key: `new_ticket_${ticketNumber}`,
        message: `New Support Ticket Submitted: ${ticketNumber} (${ticketType})`,
        type: "new_ticket",
        ticketId: ticketDocRef.id,
        ticketNumber: ticketNumber,
        targetRoles: ["Developer"],
        createdAt: new Date().toISOString(),
        readBy: []
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Error submitting ticket to developer:", err);
      setError("Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "500px" }}>
        <div className="modal-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Bug size={20} className="text-secondary" /> Send to Developer
          </h3>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div style={{ padding: "3rem 1.5rem", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
            <div style={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", color: "#ffffff" }}>
              <Check size={36} />
            </div>
            <h4 style={{ color: "var(--text-primary)", margin: 0 }}>Ticket Submitted!</h4>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", margin: 0 }}>
              The developer has been notified. Thank you for your feedback!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
            {error && (
              <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="ticketType">Request Type *</label>
              <select
                id="ticketType"
                className="form-input"
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
                required
              >
                <option value="Bug">Bug Report</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Other">Other / General Feedback</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="ticketDescription">Description / Details *</label>
              <textarea
                id="ticketDescription"
                className="form-input"
                rows={5}
                style={{ minHeight: "120px", padding: "0.75rem" }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Explain the bug step-by-step, or detail your feature request request..."
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <Paperclip size={14} /> Add Attachments (Optional)
                </span>
                <span style={{ fontSize: "0.8rem", color: attachments.length >= 5 ? "var(--warning)" : "var(--text-muted)" }}>
                  {attachments.length}/5 files
                </span>
              </label>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label 
                  htmlFor="ticketAttachment" 
                  className="btn btn-secondary"
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    gap: "0.5rem", 
                    cursor: attachments.length >= 5 ? "not-allowed" : "pointer", 
                    width: "100%",
                    padding: "0.6rem",
                    opacity: attachments.length >= 5 ? 0.5 : 1
                  }}
                >
                  <Upload size={16} /> {attachments.length >= 5 ? "Maximum 5 Files Attached" : "Choose Files (Images/PDFs/Docs)"}
                </label>
                <input
                  type="file"
                  id="ticketAttachment"
                  multiple
                  accept="image/png, image/jpeg, application/pdf, .docx, .xlsx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  disabled={attachments.length >= 5}
                />
                
                {attachments.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.25rem" }}>
                    {attachments.map((att, idx) => (
                      <div 
                        key={idx} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          backgroundColor: "rgba(255, 255, 255, 0.03)", 
                          padding: "0.45rem 0.6rem", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)", 
                          fontSize: "0.85rem" 
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", overflow: "hidden" }}>
                          <Paperclip size={13} style={{ color: "var(--primary)", flexShrink: 0 }} />
                          <span style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={att.name}>
                            {att.name}
                          </span>
                          {att.size && (
                            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", flexShrink: 0 }}>
                              ({(att.size / 1024).toFixed(0)} KB)
                            </span>
                          )}
                        </div>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveAttachment(idx)}
                          style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", padding: "0.2rem", marginLeft: "0.5rem" }}
                          title="Remove file"
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginTop: "0.25rem" }}>
                Max 5 attachments, 5MB each. Formats: jpg, png, docx, xlsx, pdf.
              </span>
            </div>

            <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Ticket"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
